import { FC, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useUnit } from "effector-react";
import { bluetoothConnectModel } from "./model";
import { bluetoothEntity } from "../../../entities/bluetooth/model";

export const BluetoothConnect: FC = () => {
  const { t } = useTranslation();

  const [devices, searchDevices, connectToDevice, isSearching, isConnecting] =
    useUnit([
      bluetoothEntity.$devices,
      bluetoothConnectModel.searchDevices,
      bluetoothConnectModel.connectToDevice,
      bluetoothConnectModel.$searchPending,
      bluetoothConnectModel.$connectPending,
    ]);

  const [connectingMac, setConnectingMac] = useState<string | null>(null);

  useEffect(() => {
    setTimeout(() => {
      searchDevices()
    }, 1500);
  }, []);

  useEffect(() => {
    if (!connectingMac) return;

    const timeout = setTimeout(() => {
      setConnectingMac(null);
    }, 10_000);

    return () => clearTimeout(timeout);
  }, [connectingMac]);

  return (
    <motion.div
      className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 max-h-[90vh] w-full max-w-[800px] flex flex-col items-center text-center bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 text-gray-100 p-10 rounded-2xl border border-gray-800 shadow-[0_0_40px_-15px_rgba(0,0,0,0.6)] space-y-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="flex w-full items-center justify-between gap-4">
        <div className="space-y-1 text-left">
          <h1 className="text-2xl font-bold text-cyan-300 drop-shadow-[0_0_10px_rgba(34,211,238,0.4)]">
            {t("bluetooth.selection.title", "Select Bluetooth Sensor")}
          </h1>
          <p className="text-gray-400 text-sm max-w-md">
            {t(
              "bluetooth.selection.subtitle",
              "Search for nearby Bluetooth devices and connect to one to start receiving data."
            )}
          </p>
        </div>

        <button
          type="button"
          onClick={() => searchDevices()}
          disabled={isSearching ?? isConnecting}
          className="inline-flex items-center justify-center rounded-xl border border-cyan-400/60 bg-cyan-500/10 px-4 py-2 text-sm font-medium text-cyan-200 shadow-[0_0_15px_-5px_rgba(34,211,238,0.7)] transition hover:bg-cyan-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSearching
            ? t("bluetooth.selection.searching", "Searching…")
            : t("bluetooth.selection.search", "Search devices")}
        </button>
      </div>

      <div className="w-full rounded-2xl border border-gray-800 bg-black/20 p-4 max-h-[360px] overflow-y-auto">
       
        {devices.length === 0 && (
          <p className="text-sm text-gray-500">
            {t(
              "bluetooth.selection.empty",
              "No Bluetooth devices found yet. Try searching again."
            )}
          </p>
        )}

        {devices.length > 0 && (
          <ul className="space-y-3">
            {devices.map((device) => {
              const isCurrent = connectingMac === device.mac;
              const disabled = isSearching || isConnecting || Boolean(connectingMac);

              return (
              <li
                key={device.mac}
                className="flex items-center justify-between rounded-xl bg-gray-900/70 px-4 py-3 border border-gray-800"
              >
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium text-gray-100">
                    {device.name}
                  </span>
                  <span className="text-xs text-gray-500">
                    MAC: {device.mac}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (connectingMac) return;
                    setConnectingMac(device.mac);
                    connectToDevice({
                      address: device.mac,
                      name: device.name,
                    });
                  }}
                  disabled={disabled}
                  className="inline-flex items-center justify-center rounded-xl border border-cyan-400/60 bg-cyan-500/10 px-3 py-1.5 text-xs font-semibold text-cyan-200 shadow-[0_0_12px_-4px_rgba(34,211,238,0.7)] transition hover:bg-cyan-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isCurrent
                    ? t("bluetooth.selection.connecting", "Connecting…")
                    : t("bluetooth.selection.connect", "Connect")}
                </button>
              </li>
            );
            })}
          </ul>
        )}
      </div>
    </motion.div>
  );
};


